import { SyntheticEvent, useRef } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import useOutsideAlerter from '@sdk/utils/hooks/outsideAlerter';
import gettext from '@sdk/lang/gettext';
import Tooltip from '@sdk/common/tooltip/Tooltip';
import { ModalState } from '@sdk/components/ui/modal/useModalState';
import StyledSvg from '@sdk/components/ui/StyledSvg';
import theme from '@sdk/theme/light';

import EmojiIcon from '@web/assets/svg/Icons/16/emoji-icon.svg';
import { EmojiPicker as GenericEmojiPicker } from '@web/components/EmojiPicker/EmojiPicker';

import EditorToolbarButton from '../EditorToolbarButton';

type Emoji = {
  native: string;
};

const Container = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  position: relative;
`;

export type EmojiPickerPosition = 'top' | 'bottom';
type PickerContainerProps = {
  pickerPosition: EmojiPickerPosition;
};
const PickerContainer = styled.div<PickerContainerProps>`
  position: absolute;
  z-index: 1;
  ${({ pickerPosition }): string => pickerPosition === 'top' && 'bottom: 25px'};
  ${({ pickerPosition }): string => pickerPosition === 'bottom' && 'top: 28px'};
  right: -40px;
`;

type Props = {
  onSelect: (emoji: string) => void;
  pickerPosition: EmojiPickerPosition;
  toggle: () => void;
  pickerState: ModalState;
};

const EmojiPicker: React.FC<React.PropsWithChildren<Props>> = ({
  onSelect,
  pickerPosition = 'top',
  pickerState,
  toggle,
}) => {
  const pickerRef = useRef();
  const { isVisible, close } = pickerState;
  useOutsideAlerter(pickerRef, close);

  const select = (emoji: Emoji): void => {
    onSelect(emoji.native);
    close();
  };

  return (
    <Container ref={pickerRef}>
      <EditorToolbarButton
        data-for="emoji-picker-tip"
        data-tip={gettext('Add emoji')}
        data-place="top"
        isActive={isVisible}
        borderRadius={2}
        onMouseDown={(e: SyntheticEvent): void => {
          e.preventDefault();
          toggle();
        }}>
        <StyledSvg svg={EmojiIcon} color={isVisible ? theme.colors.white : theme.colors.iconGray} />
      </EditorToolbarButton>
      <Tooltip id="emoji-picker-tip" />
      {isVisible && (
        <PickerContainer pickerPosition={pickerPosition}>
          <GenericEmojiPicker onSelect={select} />
        </PickerContainer>
      )}
    </Container>
  );
};

export default EmojiPicker;
